const inrFmt = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numFmt = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const intFmt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

export const inr = (n: number | string | null | undefined) => (n == null || n === '' ? '' : inrFmt.format(Number(n)));
export const num = (n: number | string | null | undefined) => (n == null || n === '' ? '' : numFmt.format(Number(n)));
export const int = (n: number | string | null | undefined) => (n == null || n === '' ? '' : intFmt.format(Number(n)));
export const money = (n: number | string | null | undefined, currency = 'INR') => currency === 'INR' ? inr(n) : (n == null ? '' : new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(Number(n)));
export const pct = (n: number | string | null | undefined) => (n == null || n === '' ? '' : `${Number(n)}%`);

/** The books run on Indian Standard Time wherever the browser is (ADR-014): "today" is the calendar date in India. */
const TZ = 'Asia/Kolkata';
const istFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
/** The calendar date in India (YYYY-MM-DD) of an instant, by default now. */
export function istDate(d = new Date()): string {
  const p = istFmt.formatToParts(d);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
/** YYYY-MM-DD from the calendar parts of a Date built as new Date(y, m, d): calendar arithmetic, no time zone involved. */
export const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const today = () => istDate();
/** First and last day of the month holding `iso` (today by default), shifted by `offset` months. */
export const monthStart = (iso = today(), offset = 0) => localIso(new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1 + offset, 1));
export const monthEnd = (iso = today(), offset = 0) => localIso(new Date(+iso.slice(0, 4), +iso.slice(5, 7) + offset, 0));
export const thisMonthRange = () => ({ from: monthStart(), to: monthEnd() });
export const lastMonthRange = () => ({ from: monthStart(today(), -1), to: monthEnd(today(), -1) });
export const addDays = (iso: string, n: number) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return localIso(d); };

export function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00+05:30` : iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ });
}
export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: TZ });
}
export function fmtMonth(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.length === 7 ? `${iso}-01T00:00:00+05:30` : iso.length === 10 ? `${iso}T00:00:00+05:30` : iso);
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: TZ });
}

/** Financial year for a date (April start by default). */
export function fyBounds(dateIso = today(), startMonth = 4): { label: string; from: string; to: string } {
  const d = new Date(`${dateIso}T00:00:00`);
  const y = d.getMonth() + 1 >= startMonth ? d.getFullYear() : d.getFullYear() - 1;
  const from = new Date(y, startMonth - 1, 1), to = new Date(y + 1, startMonth - 1, 0);
  return { label: `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`, from: localIso(from), to: localIso(to) };
}

export const label = (s?: string | null) => (s ?? '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export const STATUS_TONE: Record<string, string> = {
  draft: 'muted', pending: 'warn', submitted: 'warn', awaiting_validation: 'warn', change_requested: 'danger', hold: 'warn', suggested: 'warn', review: 'warn', importing: 'muted',
  validated: 'info', approved: 'info', issued: 'info', matched: 'info', created: 'info', queued: 'muted', rendering: 'muted',
  partially_paid: 'info', paid: 'ok', posted: 'ok', reconciled: 'ok', rendered: 'ok', sent: 'ok', done: 'ok', accepted: 'ok', complete: 'ok', open: 'warn',
  rejected: 'danger', cancelled: 'muted', void: 'danger', reversed: 'danger', failed: 'danger', expired: 'muted', ignored: 'muted', unmatched: 'danger', missing: 'danger', declared: 'warn',
  signed: 'ok', verified: 'ok', mismatch: 'danger',
};

export function toCsv(rows: Record<string, unknown>[], columns?: string[]): string {
  if (rows.length === 0) return '';
  const cols = columns ?? Object.keys(rows[0]);
  const esc = (v: unknown) => { const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
}
export function downloadText(filename: string, text: string, mime = 'text/csv') {
  const blob = new Blob(['﻿', text], { type: `${mime};charset=utf-8` });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
